"use client"

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, Minus, ArrowRight, Activity, Mail, Bell } from 'lucide-react';
import WorkflowService from '@/api/workflow.service';
import DealService from '@/api/deal.service';
import { Separator } from '@/components/ui/separator';
import { 
  Card, 
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const formSchema = z.object({
  name: z.string().min(1, 'Workflow name is required'),
  description: z.string().optional(),
  triggerType: z.string().min(1, 'Trigger type is required'),
  isActive: z.boolean().default(true),
  conditions: z.array(
    z.object({
      field: z.string().min(1, 'Field is required'),
      operator: z.string().min(1, 'Operator is required'),
      value: z.string().min(1, 'Value is required'),
    })
  ).optional(),
  actions: z.array(
    z.object({
      actionType: z.string().min(1, 'Action type is required'),
      config: z.record(z.any()).optional(),
    })
  ).min(1, 'At least one action is required'),
});

type Condition = {
  field: string;
  operator: string;
  value: string;
};

type Action = {
  actionType: string;
  config: Record<string, any>;
};

interface CreateWorkflowFormProps {
  businessId: number;
  workflow?: any;
  onSuccess: () => void;
}

export default function CreateWorkflowForm({ businessId, workflow, onSuccess }: CreateWorkflowFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [pipelineStages, setPipelineStages] = useState<any[]>([]);
  const [isLoadingStages, setIsLoadingStages] = useState(false);
  const { toast } = useToast();
  
  const defaultConditions: Condition[] = workflow?.conditions || [];
  const defaultActions: Action[] = workflow?.actions || [{ actionType: '', config: {} }];
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: workflow?.name || '',
      description: workflow?.description || '',
      triggerType: workflow?.triggerType || '',
      isActive: workflow?.isActive ?? true,
      conditions: defaultConditions,
      actions: defaultActions,
    },
  });
  
  const watchTriggerType = form.watch('triggerType');
  
  useEffect(() => {
    // Reset conditions when trigger type changes
    if ((form.getValues('conditions')?.length ?? 0) > 0) {
      form.setValue('conditions', []);
    }
    
    // If trigger type is deal_stage_changed, fetch pipeline stages
    if (watchTriggerType === 'deal_stage_changed') {
      fetchPipelineStages();
    }
  }, [watchTriggerType, form]);
  
  const fetchPipelineStages = async () => {
    setIsLoadingStages(true);
    try {
      const response = await DealService.getPipeline(businessId);
      
      if (response.data.result === 'success' && response.data.pipeline?.stages) {
        setPipelineStages(response.data.pipeline.stages);
      }
    } catch (error) {
      console.error("Error fetching pipeline stages:", error);
      toast({
        title: "Error",
        description: "Failed to load pipeline stages.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingStages(false);
    }
  };
  
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      // Clean up empty or invalid conditions
      const cleanedConditions = values.conditions?.filter(
        condition => condition.field && condition.operator && condition.value
      ) || [];
      
      // Clean up empty configs in actions
      const cleanedActions = values.actions.map(action => ({
        ...action,
        config: Object.fromEntries(
          Object.entries(action.config || {}).filter(([_, value]) => value !== '')
        ),
      }));
      
      const workflowData = {
        name: values.name,
        description: values.description || '',
        triggerType: values.triggerType,
        isActive: values.isActive,
        conditions: cleanedConditions,
        actions: cleanedActions,
      };
      
      let response;
      if (workflow) {
        response = await WorkflowService.updateWorkflow(workflow.id, workflowData);
      } else {
        response = await WorkflowService.createWorkflow(businessId, workflowData);
      }
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: `Workflow ${workflow ? 'updated' : 'created'} successfully!`,
        });
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.data.message || `Failed to ${workflow ? 'update' : 'create'} workflow.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(`Error ${workflow ? 'updating' : 'creating'} workflow:`, error);
      toast({
        title: "Error",
        description: `An error occurred while ${workflow ? 'updating' : 'creating'} the workflow.`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const addCondition = () => {
    const currentConditions = form.getValues('conditions') || [];
    form.setValue('conditions', [
      ...currentConditions, 
      { field: '', operator: '', value: '' }
    ]);
  };
  
  const removeCondition = (index: number) => {
    const currentConditions = form.getValues('conditions') || [];
    form.setValue('conditions', currentConditions.filter((_, i) => i !== index));
  };
  
  const addAction = () => {
    const currentActions = form.getValues('actions');
    form.setValue('actions', [
      ...currentActions, 
      { actionType: '', config: {} }
    ]);
  };
  
  const removeAction = (index: number) => {
    const currentActions = form.getValues('actions');
    if (currentActions.length > 1) {
      form.setValue('actions', currentActions.filter((_, i) => i !== index));
    }
  };
  
  const getFieldOptions = () => {
    const triggerType = form.getValues('triggerType');
    
    switch (triggerType) {
      case 'customer_created':
        return [
          { value: 'firstName', label: 'First Name' },
          { value: 'lastName', label: 'Last Name' },
          { value: 'emailAddress', label: 'Email Address' },
          { value: 'companyName', label: 'Company Name' },
          { value: 'country', label: 'Country' },
        ];
      case 'deal_stage_changed':
        return [
          { value: 'fromStageId', label: 'From Stage' },
          { value: 'toStageId', label: 'To Stage' },
          { value: 'value', label: 'Deal Value' },
        ];
      case 'reminder_due':
        return [
          { value: 'type', label: 'Reminder Type' },
          { value: 'customerId', label: 'Related to Customer' },
          { value: 'dealId', label: 'Related to Deal' },
        ];
      default:
        return [];
    }
  };
  
  const getOperatorOptions = (field: string) => {
    if (field.includes('Id') || field === 'type') {
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
      ];
    } else if (field === 'value') {
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'greater_than', label: 'Greater Than' },
        { value: 'less_than', label: 'Less Than' },
      ];
    } else {
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'starts_with', label: 'Starts With' },
      ];
    }
  };
  
  const getValueOptions = (field: string) => {
    if (field === 'fromStageId' || field === 'toStageId') {
      return pipelineStages.map(stage => ({
        value: stage.id.toString(),
        label: stage.name,
      }));
    } else if (field === 'type' && form.getValues('triggerType') === 'reminder_due') {
      return [
        { value: 'birthday', label: 'Birthday' },
        { value: 'follow-up', label: 'Follow-up' },
        { value: 'meeting', label: 'Meeting' },
        { value: 'task', label: 'Task' },
      ];
    }
    return [];
  };
  
  // Helper to determine if a field has predefined value options
  const hasValueOptions = (field: string) => {
    return field === 'fromStageId' || field === 'toStageId' || 
           (field === 'type' && form.getValues('triggerType') === 'reminder_due');
  };
  
  const getActionConfig = (actionType: string) => {
    switch (actionType) {
      case 'send_email':
        return [
          { key: 'subject', label: 'Subject', type: 'text' },
          { key: 'message', label: 'Message', type: 'textarea' },
        ];
      case 'create_task':
        return [
          { key: 'title', label: 'Task Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'textarea' },
          { key: 'dueDate', label: 'Due Days (from trigger)', type: 'number' },
        ];
      case 'create_reminder':
        return [
          { key: 'message', label: 'Reminder Message', type: 'text' },
          { key: 'type', label: 'Reminder Type', type: 'select', options: [
            { value: 'follow-up', label: 'Follow-up' },
            { value: 'task', label: 'Task' },
            { value: 'meeting', label: 'Meeting' },
          ]},
          { key: 'daysLater', label: 'Days Later', type: 'number' },
        ];
      default:
        return [];
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Workflow Name</FormLabel>
                <FormControl>
                  <Input placeholder="E.g., Welcome New Customers" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between border rounded-md p-4">
                <div className="space-y-0.5">
                  <FormLabel>Active Status</FormLabel>
                  <FormDescription>
                    Enable or disable this workflow
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description (Optional)</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Describe what this workflow does..."
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="triggerType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Trigger</FormLabel>
              <FormDescription>
                When should this workflow run?
              </FormDescription>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                <FormControl>
                  <Card 
                    className={`cursor-pointer hover:border-primary hover:bg-primary/5 ${
                      field.value === 'customer_created' ? 'border-primary bg-primary/5' : ''
                    }`}
                    onClick={() => field.onChange('customer_created')}
                  >
                    <CardHeader className="pb-2 pt-4">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Plus className="w-4 h-4 text-green-500" />
                        New Customer
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Triggered when a new customer is created
                      </CardDescription>
                    </CardContent>
                  </Card>
                </FormControl>
                
                <FormControl>
                  <Card 
                    className={`cursor-pointer hover:border-primary hover:bg-primary/5 ${
                      field.value === 'deal_stage_changed' ? 'border-primary bg-primary/5' : ''
                    }`}
                    onClick={() => field.onChange('deal_stage_changed')}
                  >
                    <CardHeader className="pb-2 pt-4">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-blue-500" />
                        Deal Stage Changed
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Triggered when a deal moves between stages
                      </CardDescription>
                    </CardContent>
                  </Card>
                </FormControl>
                
                <FormControl>
                  <Card 
                    className={`cursor-pointer hover:border-primary hover:bg-primary/5 ${
                      field.value === 'reminder_due' ? 'border-primary bg-primary/5' : ''
                    }`}
                    onClick={() => field.onChange('reminder_due')}
                  >
                    <CardHeader className="pb-2 pt-4">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Activity className="w-4 h-4 text-purple-500" />
                        Reminder Due
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-xs">
                        Triggered when a reminder is due
                      </CardDescription>
                    </CardContent>
                  </Card>
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {form.getValues('triggerType') && (
          <>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Conditions</h3>
                  <p className="text-sm text-muted-foreground">
                    Define when this workflow should run (optional)
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCondition}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Condition
                </Button>
              </div>
              
              {(!form.getValues('conditions') || (form.getValues('conditions')?.length ?? 0) === 0) && (
                <div className="text-center py-4 border rounded-md bg-muted/20">
                  <p className="text-muted-foreground text-sm">
                    No conditions defined - workflow will run for all {form.getValues('triggerType').replace(/_/g, ' ')} events
                  </p>
                </div>
              )}
              
              {form.getValues('conditions')?.map((_, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="grid grid-cols-3 gap-3 flex-1">
                    <FormField
                      control={form.control}
                      name={`conditions.${index}.field`}
                      render={({ field }) => (
                        <FormItem>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select field" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {getFieldOptions().map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name={`conditions.${index}.operator`}
                      render={({ field }) => (
                        <FormItem>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                            disabled={!form.getValues(`conditions.${index}.field`)}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select operator" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {getOperatorOptions(form.getValues(`conditions.${index}.field`)).map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name={`conditions.${index}.value`}
                      render={({ field }) => {
                        const conditionField = form.getValues(`conditions.${index}.field`);
                        const hasOptions = hasValueOptions(conditionField);
                        
                        return (
                          <FormItem>
                            {hasOptions ? (
                              <Select 
                                onValueChange={field.onChange} 
                                defaultValue={field.value}
                                disabled={!form.getValues(`conditions.${index}.operator`)}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select value" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {getValueOptions(conditionField).map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <FormControl>
                                <Input 
                                  placeholder="Value" 
                                  {...field}
                                  disabled={!form.getValues(`conditions.${index}.operator`)}
                                />
                              </FormControl>
                            )}
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  </div>
                  
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCondition(index)}
                    className="mt-0.5"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            
            <Separator />
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Actions</h3>
                  <p className="text-sm text-muted-foreground">
                    What should happen when this workflow runs
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAction}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Action
                </Button>
              </div>
              
              {form.getValues('actions').map((_, index) => (
                <Card key={index} className="relative">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-md flex justify-between">
                      <span>Action {index + 1}</span>
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeAction(index)}
                          className="h-6 w-6 absolute top-2 right-2"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name={`actions.${index}.actionType`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Action Type</FormLabel>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                            <FormControl>
                              <Card 
                                className={`cursor-pointer hover:border-primary hover:bg-primary/5 ${
                                  field.value === 'send_email' ? 'border-primary bg-primary/5' : ''
                                }`}
                                onClick={() => {
                                  field.onChange('send_email');
                                  form.setValue(`actions.${index}.config`, {});
                                }}
                              >
                                <CardHeader className="pb-2 pt-4">
                                  <CardTitle className="text-sm flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-blue-500" />
                                    Send Email
                                  </CardTitle>
                                </CardHeader>
                              </Card>
                            </FormControl>
                            
                            <FormControl>
                              <Card 
                                className={`cursor-pointer hover:border-primary hover:bg-primary/5 ${
                                  field.value === 'create_task' ? 'border-primary bg-primary/5' : ''
                                }`}
                                onClick={() => {
                                  field.onChange('create_task');
                                  form.setValue(`actions.${index}.config`, {});
                                }}
                              >
                                <CardHeader className="pb-2 pt-4">
                                  <CardTitle className="text-sm flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-purple-500" />
                                    Create Task
                                  </CardTitle>
                                </CardHeader>
                              </Card>
                            </FormControl>
                            
                            <FormControl>
                              <Card 
                                className={`cursor-pointer hover:border-primary hover:bg-primary/5 ${
                                  field.value === 'create_reminder' ? 'border-primary bg-primary/5' : ''
                                }`}
                                onClick={() => {
                                  field.onChange('create_reminder');
                                  form.setValue(`actions.${index}.config`, {});
                                }}
                              >
                                <CardHeader className="pb-2 pt-4">
                                  <CardTitle className="text-sm flex items-center gap-2">
                                    <Bell className="w-4 h-4 text-orange-500" />
                                    Create Reminder
                                  </CardTitle>
                                </CardHeader>
                              </Card>
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {form.getValues(`actions.${index}.actionType`) && (
                      <div className="space-y-4 pt-2">
                        {getActionConfig(form.getValues(`actions.${index}.actionType`)).map((configItem) => (
                          <div key={configItem.key}>
                            {configItem.type === 'textarea' ? (
                              <FormField
                                control={form.control}
                                name={`actions.${index}.config.${configItem.key}`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{configItem.label}</FormLabel>
                                    <FormControl>
                                      <Textarea 
                                        className="resize-none"
                                        placeholder={configItem.label}
                                        {...field}
                                        value={field.value || ''}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            ) : configItem.type === 'select' ? (
                              <FormField
                                control={form.control}
                                name={`actions.${index}.config.${configItem.key}`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{configItem.label}</FormLabel>
                                    <Select 
                                      onValueChange={field.onChange} 
                                      defaultValue={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue placeholder={`Select ${configItem.label}`} />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {configItem.options?.map((option: any) => (
                                          <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            ) : (
                              <FormField
                                control={form.control}
                                name={`actions.${index}.config.${configItem.key}`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{configItem.label}</FormLabel>
                                    <FormControl>
                                      <Input 
                                        type={configItem.type === 'number' ? 'number' : 'text'}
                                        placeholder={configItem.label}
                                        {...field}
                                        value={field.value || ''}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
        
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onSuccess}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {workflow ? 'Update Workflow' : 'Create Workflow'}
          </Button>
        </div>
      </form>
    </Form>
  );
} 